/**
 * Typed API client for e-signature (Epic 226 "utilizable"). Mirrors
 * internal/esign + the sealed certificate from internal/utilizable.
 *
 * Authenticated senders create/send envelopes and read the audit trail; signers
 * use the public token surface (no auth) to view, sign, or decline.
 */

import { apiClient } from '@/lib/apiClient';

export interface Envelope {
  id: string;
  title: string;
  status: string;
  expiresAt: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface SignerDef {
  order: number;
  name: string;
  email: string;
  role: string;
}

export interface SignerToken {
  signerId: string;
  email: string;
  accessToken: string;
  signUrl: string;
}

export interface EnvelopeCreated {
  id: string;
  signers: SignerToken[];
}

export interface AuditEntry {
  eventType: string;
  actor: string | null;
  ipAddress: string | null;
  details: unknown;
  occurredAt: string;
}

export async function listEnvelopes(): Promise<Envelope[]> {
  const { data } = await apiClient.get<Envelope[]>('/esign/envelopes');
  return data;
}

export interface CreateEnvelopeRequest {
  title: string;
  attachmentId?: string;
  entityType?: string;
  entityId?: string;
  expiresAt?: string;
  signers: SignerDef[];
}

export async function createEnvelope(req: CreateEnvelopeRequest): Promise<EnvelopeCreated> {
  const { data } = await apiClient.post<EnvelopeCreated>('/esign/envelopes', req);
  return data;
}

export async function sendEnvelope(id: string): Promise<void> {
  await apiClient.post(`/esign/envelopes/${id}/send`, {});
}

export async function auditTrail(id: string): Promise<AuditEntry[]> {
  const { data } = await apiClient.get<AuditEntry[]>(`/esign/envelopes/${id}/audit`);
  return data;
}

/** The sealed certificate-of-completion HTML URL (opened in a new tab). */
export function certificateUrl(envelopeId: string): string {
  return `/api/v1/esign/flow/${envelopeId}/certificate.html`;
}

// ── Public signer surface (unauthenticated; the token is the credential) ──

export interface SignerView {
  signerId: string;
  envelopeId: string;
  envelopeTitle: string;
  signerName: string;
  signerEmail: string;
  signed: boolean;
  declined: boolean;
}

export async function viewSigner(token: string): Promise<SignerView> {
  const { data } = await apiClient.get<SignerView>(`/public/esign/${token}`);
  return data;
}

export async function signEnvelope(token: string, signatureDataUrl: string): Promise<void> {
  await apiClient.post(`/public/esign/${token}/sign`, { signatureDataUrl });
}

export async function declineEnvelope(token: string, reason: string): Promise<void> {
  await apiClient.post(`/public/esign/${token}/decline`, { reason });
}
